import { ref } from 'vue';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  skipTransition?: boolean;
}

// Timing constants (ms)
const MESSAGE_DELAY = 800;
const TYPING_INDICATOR_DURATION = 700;
const MS_PER_CHAR = 12;
const PAUSE_BEFORE_RESET = 3000;
const FADE_DURATION = 400;
const FADE_TRANSITION = 200;

export function useDemoChat() {
  const visibleMessages = ref<Message[]>([]);
  const isTyping = ref(false);
  const typingContent = ref('');
  const typingRole = ref<'user' | 'assistant'>('assistant');
  const demoVisible = ref(false);

  let animationTimer: ReturnType<typeof setTimeout> | null = null;

  async function sleep(ms: number) {
    return new Promise((resolve) => {
      animationTimer = setTimeout(resolve, ms);
    });
  }

  async function typeText(text: string) {
    typingContent.value = '';
    for (let i = 1; i <= text.length; i++) {
      typingContent.value = text.slice(0, i);
      await sleep(MS_PER_CHAR);
    }
  }

  async function showUserMessage(message: Message) {
    typingRole.value = 'user';
    isTyping.value = true;
    await sleep(TYPING_INDICATOR_DURATION);
    isTyping.value = false;
    await sleep(FADE_TRANSITION);
    visibleMessages.value.push(message);
  }

  async function showAssistantMessage(message: Message) {
    typingRole.value = 'assistant';
    isTyping.value = true;
    await sleep(TYPING_INDICATOR_DURATION);
    isTyping.value = false;
    await sleep(FADE_TRANSITION);
    await typeText(message.content);
    typingContent.value = '';
    visibleMessages.value.push({ ...message, skipTransition: true });
  }

  function reset() {
    visibleMessages.value = [];
    isTyping.value = false;
    typingContent.value = '';
  }

  async function fadeIn() {
    demoVisible.value = true;
    await sleep(FADE_DURATION);
  }

  async function fadeOutAndRestart(runDemo: () => Promise<void>) {
    await sleep(PAUSE_BEFORE_RESET);
    demoVisible.value = false;
    await sleep(FADE_DURATION);
    runDemo();
  }

  function cleanup() {
    if (animationTimer) {
      clearTimeout(animationTimer);
    }
  }

  return {
    // State
    visibleMessages,
    isTyping,
    typingContent,
    typingRole,
    demoVisible,
    // Functions
    sleep,
    showUserMessage,
    showAssistantMessage,
    reset,
    fadeIn,
    fadeOutAndRestart,
    cleanup,
    // Constants (exposed for custom delays)
    MESSAGE_DELAY,
  };
}
